import {
   MutationFunction,
   useMutation,
   UseMutationOptions,
   UseMutationResult,
   useQueryClient,
} from '@tanstack/react-query';
import { MutationInvalidationMap } from './MutationInvalidationMap';

import { ErrorMessages } from '~/monolith-api';
import { useApiErrorMessageToast } from './useApiErrorMessageToast';
import { ApiClient } from '@dozuki/web-js/utils';
import { useRouter } from 'next/router';

export type UseRegisteredMutationOptions<TData, TError, TVariables, TContext> = UseMutationOptions<
   TData,
   TError,
   TVariables,
   TContext
> & {
   errorMessages?: ErrorMessages;
};

/**
 * useRegisteredMutation
 * @deprecated - Please see ~/pages/Courses for an example of how to model queries and invalidation
 * @description - Used in place of 'useMutation'. Automatically invalidating queries
 *     according to the MutationInvalidationMap after a successful mutation
 *
 * @usage -
 *  useRegisteredMutation(key: keyof typeof MutationInvalidationMap, mutationFn, options);
 */
export function useRegisteredMutation<
   TData = unknown,
   TError = unknown,
   TVariables = void,
   TContext = unknown
>(
   keys: keyof typeof MutationInvalidationMap | (keyof typeof MutationInvalidationMap)[],
   queryFunction: MutationFunction<TData, TVariables>,
   options: UseRegisteredMutationOptions<TData, TError, TVariables, TContext> = {}
): UseMutationResult<TData, TError, TVariables, TContext> {
   const queryClient = useQueryClient();
   const router = useRouter();

   const apiErrorMessageToast = useApiErrorMessageToast(options.errorMessages);

   return useMutation(queryFunction, {
      ...options,
      onSuccess(data, ...args) {
         if (!Array.isArray(keys)) {
            keys = [keys];
         }

         for (let key of keys) {
            const invalidatedQueryKeys = MutationInvalidationMap[key];

            for (let invalidatedQueryKey of invalidatedQueryKeys) {
               queryClient.invalidateQueries([invalidatedQueryKey]);
            }
         }

         if (options.onSuccess) {
            options.onSuccess(data, ...args);
         }
      },
      onError(error, ...args) {
         if (ApiClient.isInvalidLoginError(error)) {
            router.push('/Login');
         }
         apiErrorMessageToast.handle(error);
         if (options.onError) {
            options.onError(error, ...args);
         }
      },
   });
}
